import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { addMilliseconds, differenceInSeconds, isFuture } from "date-fns";
import { useCallback, useRef, useState } from "react";

import {
  EXTENDED_RETRY_DURATION_MS,
  EXTENDED_RETRY_INTERVAL_MS,
  QUICK_RETRY_DURATION_MS,
  QUICK_RETRY_INTERVAL_MS,
  TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE,
  WORKER_NOT_CLOCKED_OUT_FROM_WORKPLACE_TIMECLOCK_ERROR_MESSAGE,
} from "./constants";

type RetryPhase = "idle" | "quick" | "extended" | "cancelling";

interface ExtendedRetryMetadata {
  shiftId: string;
  workerId: string;
  workplaceId: string;
  action: "clock-in" | "clock-out";
}

interface UseExtendedTimekeepingRetryResult<T> {
  retryPhase: RetryPhase;
  executeWithRetry: (
    actionFunction: () => Promise<T>,
    metadata: ExtendedRetryMetadata
  ) => Promise<T | undefined>;
  cancel: () => void;
}

function isRetryableError(error: unknown): boolean {
  return (
    error instanceof Error &&
    [
      TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE,
      WORKER_NOT_CLOCKED_OUT_FROM_WORKPLACE_TIMECLOCK_ERROR_MESSAGE,
    ].includes(error.message)
  );
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function useExtendedTimekeepingRetry<T>(): UseExtendedTimekeepingRetryResult<T> {
  const [retryPhase, setRetryPhase] = useState<RetryPhase>("idle");

  // Use an execution ID to uniquely identify each retry loop invocation.
  // When a new executeWithRetry call starts or cancel is called (with no
  // in-flight request), the ID is incremented, causing any previous loop
  // that is sleeping between retries to detect staleness and bail out.
  const activeExecutionIdRef = useRef(0);

  // Tracks the in-flight actionFunction() promise so that cancel() and a
  // new executeWithRetry() call can wait for it to complete instead of
  // silently discarding its result.
  const inflightPromiseRef = useRef<Promise<T> | undefined>(undefined);

  // When true, the current retry loop should stop after the in-flight
  // request completes — but still process and return a successful result.
  const cancelAfterInflightRef = useRef(false);

  const cancel = useCallback(() => {
    if (inflightPromiseRef.current) {
      // There is an in-flight request — signal the loop to stop after it
      // completes, and show a "cancelling" loading state in the meantime.
      cancelAfterInflightRef.current = true;
      setRetryPhase("cancelling");
    } else {
      // No in-flight request (loop is sleeping or idle) — cancel immediately
      // by bumping the execution ID so the sleeping loop detects staleness.
      activeExecutionIdRef.current += 1;
      setRetryPhase("idle");
    }
  }, []);

  const executeWithRetry = useCallback(
    async (
      actionFunction: () => Promise<T>,
      metadata: ExtendedRetryMetadata
    ): Promise<T | undefined> => {
      // Invalidate any previous sleeping retry loop by bumping the execution ID.
      activeExecutionIdRef.current += 1;
      const executionId = activeExecutionIdRef.current;
      // Reset any pending cancel flag from a previous cancel() call.
      cancelAfterInflightRef.current = false;

      const isStale = () => executionId !== activeExecutionIdRef.current;

      // If there is an in-flight request from a previous loop, wait for it
      // to complete. If it succeeds we return its result directly (avoiding
      // a duplicate request). If it fails with a retryable error we proceed
      // to start a fresh retry loop.
      const existingInflight = inflightPromiseRef.current;
      if (existingInflight) {
        try {
          const result = await existingInflight;

          if (isStale()) {
            return undefined;
          }

          cancelAfterInflightRef.current = false;
          setRetryPhase("idle");
          return result;
        } catch (error: unknown) {
          if (isStale()) {
            return undefined;
          }

          if (cancelAfterInflightRef.current) {
            cancelAfterInflightRef.current = false;
            setRetryPhase("idle");
            return undefined;
          }

          if (!isRetryableError(error)) {
            setRetryPhase("idle");
            throw error;
          }

          // Retryable error — fall through to start a new retry loop.
        }
      }

      const startTime = new Date();
      let attemptNumber = 0;
      let phase: RetryPhase = "quick";

      setRetryPhase(phase);

      let retryDuration = QUICK_RETRY_DURATION_MS;
      let retryInterval = QUICK_RETRY_INTERVAL_MS;

      while (isFuture(addMilliseconds(startTime, retryDuration))) {
        if (isStale()) {
          return undefined;
        }

        attemptNumber += 1;

        try {
          const promise = actionFunction();
          inflightPromiseRef.current = promise;
          const result = await promise;
          inflightPromiseRef.current = undefined;

          if (isStale()) {
            return undefined;
          }

          if (cancelAfterInflightRef.current) {
            cancelAfterInflightRef.current = false;
            setRetryPhase("idle");
            return result;
          }

          setRetryPhase("idle");
          return result;
        } catch (error: unknown) {
          inflightPromiseRef.current = undefined;

          if (isStale()) {
            return undefined;
          }

          if (cancelAfterInflightRef.current) {
            cancelAfterInflightRef.current = false;
            setRetryPhase("idle");
            return undefined;
          }

          if (!isRetryableError(error)) {
            setRetryPhase("idle");
            throw error;
          }

          logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_INTEGRATION_RETRY_ATTEMPT, {
            shiftId: metadata.shiftId,
            workerId: metadata.workerId,
            workplaceId: metadata.workplaceId,
            action: metadata.action,
            attemptNumber,
            phase,
            elapsedTimeInSeconds: differenceInSeconds(new Date(), startTime),
          });

          if (phase === "quick" && !isFuture(addMilliseconds(startTime, retryDuration))) {
            retryDuration = EXTENDED_RETRY_DURATION_MS + QUICK_RETRY_DURATION_MS;
            retryInterval = EXTENDED_RETRY_INTERVAL_MS;

            phase = "extended";
            setRetryPhase(phase);
          }

          if (isFuture(addMilliseconds(startTime, retryDuration - retryInterval))) {
            await sleep(retryInterval);
          }
        }
      }

      logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_INTEGRATION_RETRY_EXHAUSTED, {
        shiftId: metadata.shiftId,
        workerId: metadata.workerId,
        workplaceId: metadata.workplaceId,
        action: metadata.action,
        totalAttempts: attemptNumber,
        totalElapsedTimeInSeconds: differenceInSeconds(new Date(), startTime),
      });

      setRetryPhase("idle");
      throw new Error(TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE);
    },
    []
  );

  return {
    retryPhase,
    executeWithRetry,
    cancel,
  };
}
